import React, { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

import { cn } from '../../../shared/cn';
import type { ButtonProps, ButtonSize, ButtonVariant } from '../Button.types';

const iconSizeClasses: Record<ButtonSize, string> = {
  small: 'h-8 w-8 p-0',
  middle: 'h-9 w-9 p-0',
  large: 'h-11 w-11 p-0',
};

const sizeClasses: Record<ButtonSize, string> = {
  small: 'min-h-8 px-3 py-1.5 text-sm',
  middle: 'min-h-10 px-4 py-2 text-[14px]',
  large: 'min-h-11 px-4 py-2.5 text-[15px]',
};

const variantClasses: Record<ButtonVariant, string> = {
  primary: 'bg-[var(--Purple-300)] text-white border border-[color-mix(in_srgb,var(--Purple-300)_80%,#0000)] hover:brightness-110',
  secondary: 'bg-[var(--Gray-100)] text-[var(--Gray-900)] border border-[var(--Gray-300)] hover:shadow',
  outline: 'bg-white text-[var(--Purple-700)] border border-[var(--Gray-300)] hover:shadow',
  'dark-outline':
    'border border-[rgba(255,255,255,0.18)] bg-[rgba(255,255,255,0.03)] text-[rgba(255,255,255,0.9)] shadow-none ' +
    'hover:bg-[rgba(0,0,0,0.28)] hover:border-[rgba(255,255,255,0.22)] ' +
    'active:bg-[rgba(0,0,0,0.4)]',
  ghost: 'bg-transparent text-[var(--Gray-900)] border border-transparent hover:bg-[var(--Gray-100)]',
  danger: 'bg-[var(--Error)] text-white border border-[color-mix(in_srgb,var(--Error)_80%,#0000)] hover:brightness-110',
  warning: 'bg-yellow-400 text-black border border-yellow-500 hover:brightness-110',
  plain: 'bg-transparent text-inherit border-0 shadow-none hover:opacity-90',
  'soft-outline':
    'bg-[rgba(255,255,255,0.04)] text-white border border-transparent shadow-none ' +
    'hover:bg-[rgba(0,0,0,0.35)] hover:border-[rgba(167,139,250,0.75)] hover:text-[rgba(167,139,250,0.95)] ' +
    'active:bg-[rgba(0,0,0,0.5)]',

  // NEW: Toggle-like blue button (strong hover/active)
  blue: 'bg-blue-600 text-white border border-blue-700 hover:bg-blue-700 active:bg-blue-800',

  // NEW: Toggle-like white button (strong hover/active)
  white: 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-200 hover:border-slate-400 active:bg-slate-300',
  green: 'bg-emerald-700 text-white border border-emerald-800 hover:bg-emerald-800 active:bg-emerald-900',
};

const base =
  'inline-flex items-center justify-center gap-2 rounded-lg shadow-sm transition select-none ' +
  'cursor-pointer disabled:cursor-not-allowed ' +
  'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--Purple-300)] ' +
  'disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:brightness-100 disabled:hover:shadow-sm';

const spinner = 'animate-spin inline-block h-[1em] w-[1em] align-[-0.125em] border-[2px] border-current border-t-transparent rounded-full';

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ size = 'middle', variant = 'outline', icon = false, loading = false, block = false, leftIcon, rightIcon, className, children, disabled, ...props }, ref) => {
    const isDisabled = disabled || loading;

    const sizeClass = icon ? iconSizeClasses[size] : sizeClasses[size];

    const classes = twMerge(
      cn(
        base,
        sizeClass,
        icon && 'gap-0', // optional: tighter for icon-only
        variantClasses[variant],
        block && 'w-full',
      ),
      className,
    );

    const isTextChild = typeof children === 'string' || typeof children === 'number';

    return (
      <button ref={ref} className={classes} disabled={isDisabled} aria-busy={loading || undefined} {...props}>
        {loading ? <span className={spinner} aria-hidden='true' /> : (leftIcon ?? null)}
        {isTextChild ? <span className='whitespace-nowrap'>{children}</span> : children}
        {rightIcon ?? null}
      </button>
    );
  },
);

Button.displayName = 'Button';
/*
 
    bg-[rgba(255,255,255,0.04)] text-white
    border border-transparent
    hover:bg-[rgba(0,0,0,0.35)]
    hover:border-[rgba(167,139,250,0.75)]
    hover:text-[rgba(167,139,250,0.95)]
    active:bg-[rgba(0,0,0,0.5)]
    shadow-none
*/
